"use client";

import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import { cn } from "@/lib/utils";

import type { NavItem, NavSection } from "@/features/docs/types";

/**
 * The docs sidebar. `sections` is required rather than defaulted to the whole
 * tree: the sidebar is scoped per subtree (see `nav.ts`), and a default would
 * quietly hand every caller the global nav again.
 *
 * Two things changed when the docs template redesign landed (variant C /
 * Runpod, 2026-08-03):
 *
 * 1. **Nesting is back.** The legacy sidebar is a collapsible tree three levels
 *    deep (Agent → Actions → Touch); v5 had flattened it to one list of dotted
 *    names (`agent.actions.touch`), which is the same links with the structure
 *    thrown away. A branch renders open when the current page is inside it, so
 *    the sidebar never hides your own location behind a collapsed parent.
 * 2. **Paint.** Rows are soft pills with a left-to-right primary tint when
 *    active, section labels carry an accent bar, and the whole nav sits in a
 *    floating panel (supplied by `DocsSidebarShell`).
 */

/**
 * True when `pathname` is this item or anything under it.
 *
 * The URL-prefix test matters as much as the listed children: some reference
 * pages exist without a nav entry (`actions/adb` is reached from a card on the
 * actions overview, not the sidebar). Matching listed hrefs alone left those
 * pages with the whole Agent → Actions branch collapsed, so the sidebar gave no
 * clue where you were. The reference tests the same prefix.
 */
function containsPath(item: NavItem, pathname: string): boolean {
  if (item.href === pathname || pathname.startsWith(`${item.href}/`)) return true;
  return (item.items ?? []).some((child) => containsPath(child, pathname));
}

function NavRow({
  item,
  pathname,
  level,
}: {
  item: NavItem;
  pathname: string;
  level: number;
}) {
  const active = pathname === item.href;
  const hasChildren = (item.items?.length ?? 0) > 0;
  const [open, setOpen] = useState(() => containsPath(item, pathname));

  return (
    <div>
      <div className="flex items-center">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${item.title}`}
            className="mr-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight
              aria-hidden="true"
              className={cn(
                "size-3 transition-transform duration-200",
                open && "rotate-90",
              )}
            />
          </button>
        ) : null}
        {/* Keeps childless rows aligned with their chevron-bearing siblings. */}
        {!hasChildren && level > 0 ? (
          <span aria-hidden="true" className="w-6 shrink-0" />
        ) : null}
        <Link
          href={item.href}
          aria-current={active ? "page" : undefined}
          className={cn(
            "flex flex-1 items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-[0.8125rem] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            active
              ? // The identifying cue is the SOLID --nav-active inset ring
                // (5.21:1 on --card); the /10 wash behind it is decorative
                // reinforcement only. Both were --primary alpha steps before —
                // /10 and /30 measure 1.06:1 and 1.22:1, so nothing here marked
                // the active page to 1.4.11. Missed by every earlier sweep
                // because an active item only exists on a docs LEAF page.
                "bg-nav-active/10 font-semibold text-foreground shadow-[inset_0_0_0_1px_hsl(var(--nav-active))]"
              : "text-docs-nav-fg hover:bg-accent hover:text-foreground",
          )}
        >
          <span className="truncate">{item.title}</span>
          {item.badge ? (
            <span className="ml-2 shrink-0 rounded-full bg-warning/15 px-1.5 py-0.5 text-[0.625rem] font-semibold text-warning-text">
              {item.badge}
            </span>
          ) : null}
        </Link>
      </div>
      {hasChildren && open ? (
        <div className="ml-4 mt-1 space-y-1">
          {item.items!.map((child) => (
            <NavRow
              key={child.href}
              item={child}
              pathname={pathname}
              level={level + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function DocsSidebar({
  sections,
  label,
}: {
  sections: NavSection[];
  label: string;
}) {
  const pathname = usePathname();

  return (
    <nav aria-label={label}>
      {sections.map((section) => (
        <div key={section.title} className="mb-6 last:mb-0">
          {/* The accent bar is the section marker — an uppercase micro-label on
           * its own reads as another row once you're scrolled into the middle
           * of a ~60-item tree. `--docs-label` is the AA-safe blue-grey; a
           * dimmed `muted-foreground` measured under 3:1 at this size. */}
          <h3 className="mb-2.5 flex items-center gap-2 px-2 text-[0.625rem] font-bold uppercase tracking-[0.18em] text-docs-label before:h-3 before:w-[3px] before:rounded-full before:bg-primary before:content-['']">
            {section.title}
          </h3>
          <div className="space-y-1">
            {section.items.map((item) => (
              <NavRow
                key={item.href}
                item={item}
                pathname={pathname}
                level={0}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
